import Phaser from 'phaser';
import i18next from 'i18next';
import { GAME_WIDTH, GAME_HEIGHT, FONT_STACK } from '@/game/config';
import { getBackground, getBestRocket, setBestRocket } from '@/game/storage';
import { Starfield } from '@/game/objects/Starfield';
import { addRoundedLabelBg } from '@/game/ui/roundedLabelBg';
import { addMuteToggle } from '@/game/ui/muteToggle';
import { scriptFontFamily } from '@/game/fonts';
import { maybeShowBuyPrompt } from '@/game/ui/buyPrompt';
import { maybeAskForRating } from '@/game/rating';
import { trackRocketStart, trackRocketRound, trackRocketEnd } from '@/analytics';
import {
  playPopSound,
  playStarSound,
  playThudSound,
  playRecordSound,
  playRoundCompleteSound,
  speakRulePrompt,
  cancelRulePrompt,
  startBackdropAmbience,
  stopBackdropAmbience,
} from '@/game/audio';
import { RocketClock, countWords, readingUnits, formatSeconds, displayedSeconds } from '@/game/rocket/clock';
import {
  buildQuestions, comprehensionPack, dealQuestion, type DealtQuestion, type RocketQuestion,
} from '@/game/rocket/deal';
import { redirectIfWebLimit } from '@/game/ui/webLimitCard';

interface RocketInit {
  lang: string;
  /** Dev only (`?rocket=15`): start further up the clock, where it bites. */
  startRound?: number;
}

const FUEL = '⛽';
const START_FUEL = 3;

/** The rocket's flight path along the top — the clock (§4.1). */
const TRACK = { x0: 170, x1: GAME_WIDTH - 170, y: 118 };
/** Where the sentence (read phase) and the question (ask phase) sit. */
const TEXT_Y = 330;
const ANSWERS_Y = 500;

/** Taps this soon after a card opens are the tap that opened it, not a new one (§4.1). */
const TAP_GUARD_MS = 400;
/** How long a wrong answer shows the sentence again, with the right answer marked (§4.1). */
const REVEAL_MS = 2500;
const RIGHT_PAUSE_MS = 800;
const AUTO_LEAVE_MS = 4000;

type Phase = 'howto' | 'ready' | 'read' | 'ask' | 'result' | 'over';

interface AnswerButton {
  text: string;
  label: Phaser.GameObjects.Text;
  bg: Phaser.GameObjects.NineSlice;
}

/**
 * Rocket Read — the violet-Sun reward minigame (specs/rocket-read-mode.md).
 *
 * A sentence stands alone while a 🚀 crosses the top of the screen as the clock. The child reads
 * until the rocket arrives or taps when done; then the sentence is gone and a question about it
 * comes with three answers — the right one, the rival from the same sentence, and an outsider of
 * the same kind. A wrong answer costs a ⛽ of three.
 *
 * The clock is per word and adaptive (game/rocket/clock.ts): it learns the child's pace from their
 * taps and glides to meet it at round 13, so a run gets hard around rounds 15–20 for a slow reader
 * and a fast one alike. Content is the language's Comprehension pack (game/rocket/deal.ts).
 *
 * Self-contained like the other reward scenes: it touches neither RoundScene/BoardScene nor the
 * Leitner engine, and is entered only by spending a banked Rocket.
 */
export class RocketReadScene extends Phaser.Scene {
  private lang!: string;
  private round = 1;
  private fuel = START_FUEL;
  private best = 0;

  private clock = new RocketClock();
  private questions: RocketQuestion[] = [];
  private usedSentences = new Set<string>();
  private current: DealtQuestion | null = null;

  private phase: Phase = 'howto';
  private phaseAt = 0;
  /** performance.now() when the read clock started, and the budget it had. */
  private readStartedAt = 0;
  private readMs = 0;
  private readTimedOut = false;
  private flight?: Phaser.Tweens.Tween;

  private rocket!: Phaser.GameObjects.Text;
  private trail!: Phaser.GameObjects.Graphics;
  private roundText!: Phaser.GameObjects.Text;
  /** Seconds left in the reading phase, under the finish flag — the rocket's position in words. */
  private timeLeftText!: Phaser.GameObjects.Text;
  /** Last values PUT on timeLeftText, so tickTimeLeft can skip the work when neither moved. */
  private timeLeftShown = NaN;
  private timeLeftLow = false;
  private bestText!: Phaser.GameObjects.Text;
  private fuelIcons: Phaser.GameObjects.Text[] = [];
  /** Everything that belongs to one phase of one round, cleared between phases. */
  private layer!: Phaser.GameObjects.Container;
  private answers: AnswerButton[] = [];

  constructor() {
    super({ key: 'RocketRead' });
  }

  init(data: RocketInit): void {
    this.lang = data.lang;
    this.round = Math.max(1, Math.floor(data.startRound ?? 1));
    this.fuel = START_FUEL;
    this.clock = new RocketClock();
    this.usedSentences = new Set();
    this.current = null;
    this.phase = 'howto';
    this.answers = [];
    this.fuelIcons = [];
  }

  create(): void {
    if (redirectIfWebLimit(this)) return;   // free web build used up — see webLimitCard.ts
    this.best = getBestRocket();
    const pack = comprehensionPack(this.lang);
    this.questions = pack ? buildQuestions(pack) : [];
    trackRocketStart();   // web demo only

    // Always the dark sky, whatever the backdrop: the sentence has to win against it (§7).
    new Starfield(this);
    startBackdropAmbience(getBackground());
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      stopBackdropAmbience();
      cancelRulePrompt();
      this.input.off('pointerdown', this.onScreenTap, this);
    });
    this.input.setDefaultCursor('pointer');

    this.layer = this.add.container(0, 0).setDepth(20);
    this.drawHud();
    this.drawTrack();

    addMuteToggle(this, {
      depth: 50,
      onToggle: (muted) => {
        if (muted) stopBackdropAmbience();
        else startBackdropAmbience(getBackground());
      },
    });

    // One screen-wide tap handler whose meaning depends on the phase. Taps on a real button
    // (back, mute, an answer) arrive with that button in `over` and are left to it.
    this.input.on('pointerdown', this.onScreenTap, this);

    if (this.questions.length === 0) {
      // No content for this language — the menu never offers the Rocket there (§2.3), so this is a
      // dev URL at most. Leave rather than show an empty board.
      this.scene.start('Menu');
      return;
    }
    this.showHowTo();
  }

  // ── phases ───────────────────────────────────────────────────────────────────

  private setPhase(p: Phase): void {
    this.phase = p;
    this.phaseAt = performance.now();
  }

  private onScreenTap(_pointer: Phaser.Input.Pointer, over: Phaser.GameObjects.GameObject[]): void {
    if (over.length > 0) return;
    if (performance.now() - this.phaseAt < TAP_GUARD_MS) return;
    if (this.phase === 'howto') this.startRound();
    else if (this.phase === 'read') this.endReading(false);
  }

  private showHowTo(): void {
    this.clearLayer();
    this.setPhase('howto');
    const shade = this.add.rectangle(GAME_WIDTH / 2, GAME_HEIGHT / 2, GAME_WIDTH, GAME_HEIGHT, 0x000000, 0.8);
    const panel = this.add.rectangle(
      GAME_WIDTH / 2, GAME_HEIGHT / 2, GAME_WIDTH * 0.8, GAME_HEIGHT * 0.72, 0x1a1f25,
    ).setStrokeStyle(4, 0xffb870);
    const title = this.add.text(GAME_WIDTH / 2, GAME_HEIGHT / 2 - 190, `🚀 ${i18next.t('rocket.title')}`, {
      fontFamily: FONT_STACK, fontSize: '58px', color: '#ffd166', fontStyle: 'bold',
    }).setOrigin(0.5);
    const body = this.add.text(GAME_WIDTH / 2, GAME_HEIGHT / 2 - 20, i18next.t('rocket.howTo'), {
      fontFamily: scriptFontFamily(this.lang), fontSize: '30px', color: '#ffffff', align: 'center',
      wordWrap: { width: GAME_WIDTH * 0.68 }, lineSpacing: 10,
    }).setOrigin(0.5);
    const fuel = this.add.text(GAME_WIDTH / 2, GAME_HEIGHT / 2 + 100, FUEL.repeat(START_FUEL), {
      fontFamily: FONT_STACK, fontSize: '38px',
    }).setOrigin(0.5);
    const start = this.add.text(GAME_WIDTH / 2, GAME_HEIGHT / 2 + 190, i18next.t('rocket.howToStart'), {
      fontFamily: FONT_STACK, fontSize: '36px', color: '#ffffff',
      backgroundColor: '#e8894a', padding: { x: 40, y: 14 }, fontStyle: 'bold',
    }).setOrigin(0.5);
    this.layer.add([shade, panel, title, body, fuel, start]);
    this.layer.setDepth(200);
  }

  /**
   * The next round, dealt and started in one beat — there is no ready card and no countdown.
   *
   * There used to be one ("Runde 7 · 12 s", tap to start), and it was cut: between rounds it is a
   * wait the child has to tap through, and the thing it announced (how long this sentence gets) is
   * on screen anyway as the rocket's own head start. The first round is opened by the how-to card,
   * which is the one moment the child IS being asked to read something first.
   */
  private startRound(): void {
    if (this.phase === 'over') return;
    this.clearLayer();
    this.layer.setDepth(20);
    this.current = dealQuestion(this.questions, this.usedSentences);
    if (!this.current) return;
    this.roundText.setText(i18next.t('rocket.round', { n: this.round }));
    this.parkRocket();
    this.startReading();
  }

  private startReading(): void {
    const dealt = this.current;
    if (!dealt) return;
    this.setPhase('read');
    playPopSound();

    const units = readingUnits(dealt.q.sentence, this.lang);
    this.readMs = this.clock.readMs(this.round, units);
    this.readTimedOut = false;
    this.readStartedAt = performance.now();
    this.timeLeftShown = displayedSeconds(this.readMs);
    this.timeLeftLow = false;
    this.timeLeftText.setText(i18next.t('rocket.seconds', { s: formatSeconds(this.readMs, this.lang) }))
      .setColor('#8a96a3')
      .setVisible(true);

    const sentence = this.add.text(GAME_WIDTH / 2, TEXT_Y, dealt.q.sentence, {
      fontFamily: scriptFontFamily(this.lang), fontSize: '42px', color: '#ffffff', align: 'center',
      wordWrap: { width: 980 }, lineSpacing: 12,
    }).setOrigin(0.5);

    // "Done" is a BUTTON, not a hint. The hint it replaced ("Fertig? Tippen!", small and grey at
    // the bottom) said what to do in a size and colour that reads as decoration — and the tap it
    // asks for is half the mode: it is what tells the clock how fast this child actually reads.
    const done = this.add.text(GAME_WIDTH / 2, GAME_HEIGHT - 96, i18next.t('rocket.done'), {
      fontFamily: FONT_STACK, fontSize: '40px', color: '#062018', fontStyle: 'bold',
      backgroundColor: '#5fe0a0', padding: { x: 56, y: 20 },
    }).setOrigin(0.5).setDepth(31);
    done.setInteractive({ useHandCursor: true });
    done.on('pointerdown', () => this.endReading(false));
    done.on('pointerover', () => done.setStyle({ backgroundColor: '#7ff0b8' }));
    done.on('pointerout', () => done.setStyle({ backgroundColor: '#5fe0a0' }));
    this.tweens.add({
      targets: done, scale: 1.045, duration: 620, yoyo: true, repeat: -1, ease: 'Sine.easeInOut',
    });
    this.layer.add([sentence, done]);

    // The rocket IS the clock: its position is the fraction of the budget used (§4.1).
    this.flight = this.tweens.add({
      targets: this.rocket,
      x: TRACK.x1,
      duration: this.readMs,
      ease: 'Linear',
      onUpdate: () => { this.drawTrail(); this.tickTimeLeft(); },
      onComplete: () => this.endReading(true),
    });
  }

  private endReading(timedOut: boolean): void {
    if (this.phase !== 'read') return;
    this.flight?.stop();
    this.flight = undefined;
    this.readTimedOut = timedOut;
    const elapsed = performance.now() - this.readStartedAt;
    // What the estimator sees: the real time to the tap, or the whole budget on a timeout.
    this.readMs = timedOut ? this.readMs : Math.min(this.readMs, elapsed);
    this.showQuestion(timedOut);
  }

  private showQuestion(timedOut: boolean): void {
    const dealt = this.current;
    if (!dealt) return;
    const doAsk = (): void => {
      this.clearLayer();
      this.setPhase('ask');
      this.timeLeftText.setVisible(false);
      const question = this.add.text(GAME_WIDTH / 2, TEXT_Y - 40, dealt.q.question, {
        fontFamily: scriptFontFamily(this.lang), fontSize: '44px', color: '#ffd166', align: 'center',
        fontStyle: 'bold', wordWrap: { width: 1000 },
      }).setOrigin(0.5);
      this.layer.add(question);
      this.layoutAnswers(dealt.answers);
      // The QUESTION may be spoken — it is not what is measured. The sentence never is (§7).
      speakRulePrompt(dealt.q.question, this.lang);
    };
    if (!timedOut) { doAsk(); return; }
    // A timeout fades the sentence rather than snapping it away (§4.1).
    this.setPhase('result');
    this.tweens.add({ targets: this.layer, alpha: 0, duration: 200, onComplete: () => {
      this.layer.setAlpha(1);
      doAsk();
    } });
  }

  private layoutAnswers(texts: string[]): void {
    const gap = 380;
    this.answers = texts.map((text, i) => {
      const x = GAME_WIDTH / 2 + (i - 1) * gap;
      const label = this.add.text(x, ANSWERS_Y, text, {
        fontFamily: scriptFontFamily(this.lang), fontSize: '38px', color: '#ffffff', align: 'center',
        padding: { x: 34, y: 18 }, wordWrap: { width: 320 },
      }).setOrigin(0.5).setDepth(30);
      const bg = addRoundedLabelBg(this, label, { radius: 22, color: 0x26303b }).setDepth(29);
      label.setInteractive({ useHandCursor: true });
      label.on('pointerdown', () => this.onAnswer(text));
      label.on('pointerover', () => label.setColor('#ffd166'));
      label.on('pointerout', () => label.setColor('#ffffff'));
      bg.setScale(0.8).setAlpha(0);
      label.setScale(0.8).setAlpha(0);
      this.tweens.add({
        targets: [bg, label], scale: 1, alpha: 1, duration: 220, delay: i * 60, ease: 'Back.easeOut',
      });
      return { text, label, bg };
    });
  }

  private onAnswer(text: string): void {
    const dealt = this.current;
    if (this.phase !== 'ask' || !dealt) return;
    this.setPhase('result');
    cancelRulePrompt();
    for (const a of this.answers) a.label.disableInteractive();

    const correct = text === dealt.q.right;
    const units = readingUnits(dealt.q.sentence, this.lang);
    const budget = this.clock.budget(this.round);
    trackRocketRound({
      round: this.round, words: countWords(dealt.q.sentence), units: Math.round(units * 10) / 10,
      budgetMs: Math.round(budget * units * 1000),
      readMs: Math.round(this.readMs), timedOut: this.readTimedOut, correct,
      answerKind: dealt.q.setId.replace(/-\d+$/, ''),
    });
    this.clock.record({ tapped: !this.readTimedOut, correct, readMs: this.readMs, words: units, budget });

    const chosen = this.answers.find((a) => a.text === text);
    const right = this.answers.find((a) => a.text === dealt.q.right);
    if (correct) {
      this.mark(chosen, 0x2e7d4f, '#ffffff');
      playStarSound();
      this.boostRocket();
      this.time.delayedCall(RIGHT_PAUSE_MS, () => this.nextRound());
      return;
    }

    playThudSound();
    this.mark(chosen, 0x5a2630, '#ff9aa2');
    this.mark(right, 0x6b5410, '#ffd166');
    if (chosen) this.tweens.add({ targets: [chosen.label, chosen.bg], alpha: 0.55, duration: 250 });
    this.loseFuel();

    // Show why: the sentence comes back above the answers for a moment (§4.1).
    const again = this.add.text(GAME_WIDTH / 2, 205, dealt.q.sentence, {
      fontFamily: scriptFontFamily(this.lang), fontSize: '32px', color: '#dfe6ee', align: 'center',
      wordWrap: { width: 1000 }, lineSpacing: 8,
    }).setOrigin(0.5).setAlpha(0);
    this.layer.add(again);
    this.tweens.add({ targets: again, alpha: 1, duration: 250 });

    this.time.delayedCall(REVEAL_MS, () => {
      if (this.fuel <= 0) this.gameOver();
      else this.nextRound();
    });
  }

  private nextRound(): void {
    if (this.phase === 'over') return;
    this.round++;
    this.startRound();
  }

  /** Recolour one answer: a new plate in `fill`, the label in `color`. */
  private mark(a: AnswerButton | undefined, fill: number, color: string): void {
    if (!a) return;
    a.bg.destroy();
    a.bg = addRoundedLabelBg(this, a.label, { radius: 22, color: fill }).setDepth(29);
    a.label.setColor(color);
  }

  private clearLayer(): void {
    this.tweens.killTweensOf(this.layer.list);
    this.layer.removeAll(true);
    for (const a of this.answers) {
      this.tweens.killTweensOf([a.label, a.bg]);
      a.label.destroy();
      a.bg.destroy();
    }
    this.answers = [];
  }

  // ── the rocket ───────────────────────────────────────────────────────────────

  private drawTrack(): void {
    const g = this.add.graphics().setDepth(10);
    g.lineStyle(2, 0x3a4552, 1);
    for (let x = TRACK.x0; x < TRACK.x1; x += 18) g.lineBetween(x, TRACK.y, x + 8, TRACK.y);
    this.add.text(TRACK.x1 + 22, TRACK.y, '🏁', { fontFamily: FONT_STACK, fontSize: '30px' })
      .setOrigin(0.5).setDepth(10);
    this.timeLeftText = this.add.text(TRACK.x1 + 22, TRACK.y + 30, '', {
      fontFamily: FONT_STACK, fontSize: '22px', color: '#8a96a3',
    }).setOrigin(0.5, 0).setDepth(10).setVisible(false);
    this.trail = this.add.graphics().setDepth(11);
    // 🚀 points up-right; a quarter turn clockwise of 45° points it along the track.
    this.rocket = this.add.text(TRACK.x0, TRACK.y, '🚀', { fontFamily: FONT_STACK, fontSize: '44px' })
      .setOrigin(0.5).setAngle(45).setDepth(12);
  }

  private drawTrail(): void {
    this.trail.clear();
    const w = this.rocket.x - TRACK.x0 - 20;
    if (w <= 0) return;
    this.trail.fillStyle(0xffb870, 0.18).fillRect(TRACK.x0, TRACK.y - 5, w, 10);
    this.trail.fillStyle(0xffe2b8, 0.55).fillRect(TRACK.x0, TRACK.y - 1.5, w, 3);
  }

  /**
   * The countdown beside the flag: what is left of this sentence's budget.
   *
   * Driven from the rocket tween's onUpdate, so this runs every frame while the whole readout
   * changes at most ten times a second. Both lines below are guarded because both were expensive
   * unconditionally: `formatSeconds` builds an `Intl.NumberFormat` and `i18next.t` interpolates,
   * and `setColor` — unlike `setText`, which compares strings first — calls `updateText()` every
   * time it is invoked, re-measuring the string and re-uploading the text texture to the GPU. That
   * made the entire reading phase re-rasterize this label at 60 fps for a value that had not moved.
   */
  private tickTimeLeft(): void {
    const left = Math.max(0, this.readMs - (performance.now() - this.readStartedAt));

    const shown = displayedSeconds(left);
    if (shown !== this.timeLeftShown) {
      this.timeLeftShown = shown;
      this.timeLeftText.setText(i18next.t('rocket.seconds', { s: formatSeconds(left, this.lang) }));
    }

    const low = left <= 3000;
    if (low !== this.timeLeftLow) {
      this.timeLeftLow = low;
      this.timeLeftText.setColor(low ? '#ff9aa2' : '#8a96a3');
    }
  }

  private parkRocket(): void {
    this.tweens.killTweensOf(this.rocket);
    this.rocket.setPosition(TRACK.x0, TRACK.y).setAngle(45).setAlpha(1).setScale(1);
    this.trail.clear();
  }

  /** A right answer: the rocket boosts off the top of the screen (§4.1). */
  private boostRocket(): void {
    this.tweens.killTweensOf(this.rocket);
    this.tweens.add({
      targets: this.rocket, angle: 0, y: -60, scale: 1.3, duration: 600, ease: 'Cubic.easeIn',
    });
  }

  // ── HUD ──────────────────────────────────────────────────────────────────────

  private drawHud(): void {
    // Corner for corner with the other reward modes: back pill top-left with the lives under it,
    // the run's readouts top-right.
    const back = this.add.text(12, 16, `← ${i18next.t('nav.menu')}`, {
      fontFamily: FONT_STACK, fontSize: '24px', color: '#dfe6ee', padding: { x: 20, y: 12 },
    }).setOrigin(0, 0).setDepth(50);
    back.setInteractive({
      hitArea: new Phaser.Geom.Rectangle(-back.x, -back.y, back.width + back.x + 10, back.height + back.y + 10),
      hitAreaCallback: Phaser.Geom.Rectangle.Contains,
      useHandCursor: true,
    });
    addRoundedLabelBg(this, back);
    back.on('pointerover', () => back.setColor('#ffffff'));
    back.on('pointerout', () => back.setColor('#dfe6ee'));
    back.on('pointerdown', () => this.scene.start('Menu'));

    const fuelY = back.y + back.height + 10;
    for (let i = 0; i < START_FUEL; i++) {
      this.fuelIcons.push(this.add.text(back.x + i * 40, fuelY, FUEL, {
        fontFamily: FONT_STACK, fontSize: '32px',
      }).setDepth(40));
    }
    this.paintFuel();

    this.roundText = this.add.text(GAME_WIDTH - 24, 20, i18next.t('rocket.round', { n: this.round }), {
      fontFamily: FONT_STACK, fontSize: '28px', color: '#ffd166',
    }).setOrigin(1, 0).setDepth(40);
    this.bestText = this.add.text(GAME_WIDTH - 24, 58, this.best > 0 ? i18next.t('rocket.best', { n: this.best }) : '', {
      fontFamily: FONT_STACK, fontSize: '22px', color: '#dfe6ee',
    }).setOrigin(1, 0).setDepth(40);
  }

  private loseFuel(): void {
    this.fuel = Math.max(0, this.fuel - 1);
    const icon = this.fuelIcons[this.fuel];
    if (icon) this.tweens.add({ targets: icon, scale: 1.4, duration: 140, yoyo: true, onComplete: () => this.paintFuel() });
    else this.paintFuel();
  }

  private paintFuel(): void {
    this.fuelIcons.forEach((t, i) => t.setAlpha(i < this.fuel ? 1 : 0.18));
  }

  // ── end of run ───────────────────────────────────────────────────────────────

  private gameOver(): void {
    if (this.phase === 'over') return;
    this.setPhase('over');
    this.clearLayer();
    cancelRulePrompt();
    trackRocketEnd(this.round);   // web demo only

    const isRecord = this.round > this.best;
    if (isRecord) {
      setBestRocket(this.round);
      this.best = this.round;
      this.bestText.setText(i18next.t('rocket.best', { n: this.best }));
      playRecordSound();
    } else {
      playRoundCompleteSound();
    }

    const reached = i18next.t('rocket.gameOver', { n: this.round });
    const second = isRecord ? i18next.t('rocket.newBest') : i18next.t('rocket.best', { n: this.best });
    const overlay = this.add.text(GAME_WIDTH / 2, GAME_HEIGHT / 2, `${reached}\n${second}`, {
      fontFamily: FONT_STACK, fontSize: '56px', color: isRecord ? '#4ecdc4' : '#ffd166',
      padding: { x: 34, y: 20 }, align: 'center',
    }).setOrigin(0.5).setAlpha(0).setScale(0.8).setDepth(150);
    const overlayBg = addRoundedLabelBg(this, overlay, { radius: 24 }).setDepth(149);
    overlayBg.setAlpha(0).setScale(0.8);
    const cont = this.add.text(GAME_WIDTH / 2, GAME_HEIGHT / 2 + 140, i18next.t('rocket.continue'), {
      fontFamily: FONT_STACK, fontSize: '24px', color: '#dfe6ee', padding: { x: 16, y: 9 },
    }).setOrigin(0.5).setDepth(150);
    addRoundedLabelBg(this, cont).setDepth(149);
    this.tweens.add({ targets: [overlay, overlayBg], alpha: 1, scale: 1, duration: 300, ease: 'Back.easeOut' });

    // Tap to leave, or leave by itself — the end card must never strand a child (recipe-mode §5.3).
    // The guard is on the tap only; the timer is on the scene clock and must always be able to fire.
    const shownAt = performance.now();
    let left = false;
    const leaveNow = (): void => {
      if (left) return;
      left = true;
      this.input.off('pointerdown', onTap);
      this.scene.start('Menu');
    };
    const onTap = (): void => {
      if (performance.now() - shownAt < 900) return;
      leaveNow();
    };
    this.input.on('pointerdown', onTap);
    this.time.delayedCall(AUTO_LEAVE_MS, leaveNow);

    maybeShowBuyPrompt(this, { delayMs: 1500 });
    maybeAskForRating(this, { kind: 'rocket', rounds: this.round });
  }
}
